import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { useSurveyIdSampleSizeMap } from '~/hooks/useSurveyIdSampleSizeMap'
import { exists, findByPath, getByPath, pathAssume, pathOr } from '~/utils'

import { tabulationQueryDataAtom } from '../atoms'
import { type DataItem } from '../utils'

import { useCurrentSurveyGroup } from './useCurrentSurveyGroup'
import { useCurrentSurveys } from './useCurrentSurveys'
import { useVisibleSurveys } from './useVisibleSurveys'

type CustomerAttributeGroupsDataItem = DataItem & {
  customerAttributeGroupId: string
  idLike: string
  surveyId: string
}

export const useCustomerAttributeGroupsData =
  (): CustomerAttributeGroupsDataItem[] => {
    const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
    const surveyGroup = useCurrentSurveyGroup()
    const currentSurveys = useCurrentSurveys()
    const surveys = useVisibleSurveys()
    const surveyIdSampleSizeMap = useSurveyIdSampleSizeMap()

    const adaptedTabulationQueryDataCustomerAttributeGroups = React.useMemo(
      () =>
        surveyGroup.customerAttributeGroups.map((customerAttributeGroup) => ({
          surveyGroupCustomerAttributeGroupId: customerAttributeGroup.id,
          answers: customerAttributeGroup.customerAttributes.map(
            (customerAttribute) => ({
              customerAttribute: R.pick(customerAttribute, ['idLike', 'name']),
              counts: currentSurveys.map((survey) => {
                const getCustomerAttributeId = () => {
                  const customerAttributeId = R.pipe(
                    customerAttribute.attachedCustomerAttributes,
                    findByPath('surveyId', survey.id),
                    getByPath('customerAttributeId'),
                  )
                  if (exists(customerAttributeId)) {
                    return customerAttributeId
                  }

                  const customerAttributeGroupId = R.pipe(
                    customerAttributeGroup.attachedCustomerAttributeGroups,
                    findByPath('surveyId', survey.id),
                    pathAssume('customerAttributeGroupId'),
                  )

                  return R.pipe(
                    survey.customerAttributeGroups,
                    findByPath('id', customerAttributeGroupId),
                    pathOr('customerAttributes', []),
                    findByPath('idLike', customerAttribute.idLike),
                    pathAssume('id'),
                  )
                }

                const customerAttributeId = getCustomerAttributeId()

                return {
                  surveyId: survey.id,
                  count: R.pipe(
                    tabulationQueryData?.customerAttributes ?? [],
                    findByPath('surveyId', survey.id),
                    (data) => data?.results ?? [],
                    findByPath('byCustomerAttributeId', customerAttributeId),
                    getByPath('result.totalCount'),
                  ),
                }
              }),
            }),
          ),
        })),
      [
        currentSurveys,
        surveyGroup.customerAttributeGroups,
        tabulationQueryData,
      ],
    )

    return React.useMemo(
      () =>
        surveyGroup.customerAttributeGroups.flatMap((customerAttributeGroup) =>
          customerAttributeGroup.customerAttributes.flatMap(
            (customerAttribute) =>
              surveys.map<CustomerAttributeGroupsDataItem>((survey) => {
                const item = {
                  customerAttributeGroupId: customerAttributeGroup.id,
                  idLike: customerAttribute.idLike,
                  surveyId: survey.id,
                  percentage: null,
                  estimatedPopulation: null,
                }

                const count = R.pipe(
                  adaptedTabulationQueryDataCustomerAttributeGroups,
                  findByPath(
                    'surveyGroupCustomerAttributeGroupId',
                    customerAttributeGroup.id,
                  ),
                  pathOr('answers', []),
                  findByPath(
                    'customerAttribute.idLike',
                    customerAttribute.idLike,
                  ),
                  pathOr('counts', []),
                  findByPath('surveyId', survey.id),
                  pathOr('count', undefined),
                )

                if (!exists(count)) {
                  return item
                }

                const percentage =
                  count / (surveyIdSampleSizeMap[survey.id] ?? NaN)

                const numberOfTotalAnswers = survey.numberOfTotalAnswers ?? NaN
                const tamNumber = survey.metadata?.tamNumber ?? 0
                const tamRatio =
                  tamNumber === 0 ? 1 : tamNumber / numberOfTotalAnswers

                const estimatedPopulation = Math.round(
                  (count * tamRatio) / 1000,
                )

                return {
                  ...item,
                  percentage,
                  estimatedPopulation,
                }
              }),
          ),
        ),
      [
        adaptedTabulationQueryDataCustomerAttributeGroups,
        surveyGroup.customerAttributeGroups,
        surveyIdSampleSizeMap,
        surveys,
      ],
    )
  }
